import { evaluateRule } from "../engine/ruleEngine";
import fieldSetsSchema from "../schema/doctorConsoleFieldSets.json";
import { applyFieldConstraints } from "../engine/applySystemFieldConstraints";

const sortByOrder = (items = []) =>
  [...items].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

/** Legacy section components own grid widths; config schema grids are not applied at runtime. */
export const mergeFieldGrid = (_configGrid, fallback = { xs: 12 }) => ({
  ...fallback,
});

export const buildFieldLayoutFromSet = (fieldSet, ruleContext = {}) => {
  const allFields = sortByOrder(fieldSet?.fields || []).filter(
    (field) => field.enabled !== false,
  );
  const tableColumns = sortByOrder(fieldSet?.tableColumns || []).filter(
    (column) => column.enabled !== false,
  );

  const fieldsByKey = {};
  const fieldsById = {};
  allFields.forEach((field) => {
    fieldsById[field.id] = field;
    if (!fieldsByKey[field.key]) {
      fieldsByKey[field.key] = field;
    }
  });

  const resolveField = (keyOrId) => fieldsById[keyOrId] || fieldsByKey[keyOrId];

  return {
    fields: allFields,
    fieldsByKey,
    fieldsById,
    tableColumns,
    pattern: fieldSet?.pattern ?? null,
    isFieldVisible: (keyOrId, row = {}) => {
      const field = resolveField(keyOrId);
      if (!field) return false;
      return evaluateRule(field.visible ?? { when: "always" }, {
        ...ruleContext,
        row,
      });
    },
    getField: (keyOrId) => resolveField(keyOrId),
    isFieldRequired: (keyOrId, row = {}) => {
      const field = resolveField(keyOrId);
      if (!field) return false;
      if (!evaluateRule(field.visible ?? { when: "always" }, { ...ruleContext, row })) {
        return false;
      }
      if (field.system?.fhirRequired === true || field.system?.locked === true) {
        return field.system?.fhirRequired !== false;
      }
      return field.validation?.required === true;
    },
    isFieldLocked: (keyOrId) => {
      const field = resolveField(keyOrId);
      return field?.system?.locked === true;
    },
    getFieldLabel: (keyOrId, fallback = "", row = {}) => {
      const field = resolveField(keyOrId);
      if (!field) return fallback;
      if (field.labelWhen) {
        const alt = evaluateRule(field.labelWhen, { ...ruleContext, row })
          ? field.labelWhen.label
          : null;
        if (alt) return alt;
      }
      return field.label ?? fallback;
    },
    getFieldGrid: (keyOrId, fallback = { xs: 12 }) =>
      mergeFieldGrid(resolveField(keyOrId)?.grid, fallback),
    getValidationMessage: (keyOrId, fallback = "") =>
      resolveField(keyOrId)?.validation?.message ?? fallback,
    isCapabilityEnabled: (keyOrId, capability) =>
      resolveField(keyOrId)?.capabilities?.[capability] !== false,
    getVitalField: (vitalName) =>
      allFields.find((field) => field.vitalName === vitalName),
    getVitalGrid: (vitalName, fallback = { xs: 6, sm: 6, md: 4, lg: 3 }) =>
      mergeFieldGrid(
        allFields.find((field) => field.vitalName === vitalName)?.grid,
        fallback,
      ),
    isVitalVisible: (vitalName, row = {}) => {
      const field = allFields.find((f) => f.vitalName === vitalName);
      if (!field) return true;
      return evaluateRule(field.visible ?? { when: "always" }, {
        ...ruleContext,
        row,
      });
    },
  };
};

export const getDefaultFieldLayout = (fieldSetId, ruleContext = {}) => {
  const fieldSet = fieldSetsSchema.fieldSets?.[fieldSetId];
  if (!fieldSet) {
    return buildFieldLayoutFromSet({ fields: [], tableColumns: [] }, ruleContext);
  }

  const constrained = applyFieldConstraints(
    fieldSet.fields || [],
    fieldSetId,
    fieldSet.tableColumns || [],
  );

  return buildFieldLayoutFromSet(
    { ...fieldSet, fields: constrained.fields, tableColumns: constrained.tableColumns },
    ruleContext,
  );
};

export const FIELD_SET_IDS = {
  chiefComplaint: "chiefComplaintRow",
  initialObservation: "initialObservationPanel",
  medicalHistory: "medicalHistoryRow",
  medicalHistoryPresent: "medicalHistoryPresentRow",
  familyHistory: "familyHistoryRow",
  drugHistory: "drugHistoryRow",
  allergy: "allergyIntoleranceRow",
  addiction: "addictionHistoryRow",
  surgical: "surgicalHistoryRow",
  menstrual: "menstrualHistoryRow",
  adultVaccination: "adultVaccinationRow",
  examinations: "examinationsVitals",
  investigations: "investigationRow",
  diagnosis: "diagnosisRow",
  medications: "medicationRow",
  procedures: "procedureRow",
  medicalAdvice: "medicalAdviceFields",
  notes: "notesFields",
  followUp: "followUpFields",
  referral: "referralFields",
  ipdAdmission: "ipdAdmissionFields",
  attachments: "attachmentUpload",
  drawingPad: "drawingPadFields",
};
